/**
 * MANAGER-OPS-COCKPIT-V2 (V1-P6) -- the per-clinic TODAY COMMAND VIEW: the
 * 30-second manager scan ("is this clinic healthy right now, where is money
 * waiting, where is work leaking") atop /dashboard/agm -- the V1-P4 tracking
 * surface re-scoped IN PLACE (same route family, same leadership audience;
 * never a parallel page).
 *
 * ROUTER, NEVER AN EDITOR (charter Scope 2b): every tile is a READ that
 * drills to its existing canonical surface -- the clinic day board (/walkin,
 * the AGM read-down door), the command center (/dashboard/lead-cockpit, the
 * V1-P4 drill), or the leakage section below on this same page. ZERO write
 * actions anywhere on this surface (every tile's onClick is a navigation).
 *
 * EXISTING READS ONLY (charter Scope 2a): the day board read per clinic
 * (?view=board -- first consumer of the consult_started event + the
 * cancel_reason_code breakdown as COUNTS), the #247 handoff derivations
 * verbatim (to-collect / paid evidence), the manager-tracking read
 * (unassigned past escalation), and the V4-P0 sla-leakage read (counts by
 * class -- the Scope 1 seam consumption). No new backend surface.
 *
 * SCOPE: the ONE global clinic context (clinic-context.tsx) is the clinic
 * selector -- a picked clinic narrows every tile; All clinics = ONE
 * all-clinics board read over the caller's verified options
 * (DASHBOARD-FANOUT-AT-119-V1: it was one read PER CLINIC, which at a
 * 119-clinic tenant is 119 parallel requests per render), grouped by each
 * row's own clinic and folded by the SAME pure derivations (org totals can
 * never disagree with the rows they sum). The server re-tests every id
 * against the caller's own scope, so batching narrows round trips and
 * widens nothing.
 *
 * HONEST STATES (charter Scope 2d): a tile whose source read failed renders
 * an explicit unavailable state -- NEVER a fake zero (fake zeros on a money
 * surface are lies); a partial All-clinics sum NAMES the clinics it is
 * missing.
 *
 * Presentational split (the ManagerTrackingSection test pattern): the
 * default export orchestrates the fetches + navigation; ManagerOpsCockpitView
 * renders a resolved CockpitState and is pinned by static-markup tests.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  fetchManagerTracking,
  fetchSlaLeakage,
  fetchTodayAppointments,
  fetchTodayAppointmentsForClinics,
} from '@/dashboard/api';
import {
  clinicOptionLabel,
  useClinicLens,
  type ClinicOption,
} from '@/dashboard/clinic-context';
import KpiTile from '@/dashboard/ui/KpiTile';
import { statusChipLabel } from '@/dashboard/ui/status-canon';
import SectionShell from '@/dashboard/ui/SectionShell';
import {
  classMeta,
  type SlaLeakageResult,
} from '@/dashboard/sla-leakage/sla-leakage-view';
import type { ManagerTrackingView } from '@/dashboard/manager-tracking/manager-tracking-view';
import type { TodayAppointment } from '@/dashboard/types';
import {
  deriveDayCommandTiles,
  escalationForScope,
  inrLabel,
  slaClassCountsForScope,
  sumDayCommandTiles,
  type CancelReasonBreakdownRow,
  type DayCommandTiles,
  type SlaClassCount,
} from '@/dashboard/manager-tracking/manager-cockpit-view';

/** The in-page anchor of the leakage section (the SLA tiles' drill). */
export const SLA_SECTION_ANCHOR_ID = 'agm-sla-leakage';

export const DAY_TILES_UNAVAILABLE_COPY =
  'Day board unavailable - counts cannot be shown right now';
export const NO_CLINIC_SCOPE_COPY =
  'No clinic scope resolved - the day tiles need at least one clinic';
export const ESCALATION_UNAVAILABLE_COPY =
  'Escalation not derivable right now (tracking read or assignment overlay unavailable)';
export const SLA_CAPPED_NOTE_COPY =
  'Large read capped - SLA counts are partial (see the leakage section)';
export const SLA_ROW_UNAVAILABLE_COPY = 'SLA leakage read unavailable';

export interface CockpitClinicRow {
  readonly clinicId: number;
  readonly clinicLabel: string;
  readonly tiles: DayCommandTiles;
}

/** The resolved render state (see the module header honest-states law). */
export interface CockpitState {
  readonly scopeLabel: string;
  /** True when no clinic scope could be resolved at all (no options). */
  readonly noScope: boolean;
  /** The scope's tiles; null = every board read failed / no scope (the tile
   *  rows render their explicit unavailable copy, never zeros). */
  readonly tiles: DayCommandTiles | null;
  /** Per-clinic rows (All-clinics mode with >1 clinic renders the table). */
  readonly perClinic: readonly CockpitClinicRow[];
  /** Clinics whose board read failed -- NAMED, never silently dropped. */
  readonly failedClinics: readonly string[];
  /** Unassigned-past-escalation count; null = the tracking read failed. */
  readonly escalation: number | null;
  /** SLA counts by class; null = the leakage read failed. */
  readonly slaCounts: readonly SlaClassCount[] | null;
  /** True when the V4-P0 read hit its cap -- counts are partial and say so
   *  (the loader's "never a silent truncation" law; review finding 3). */
  readonly slaCapped: boolean;
  /** All-clinics mode flag (drives the per-clinic table). */
  readonly allClinics: boolean;
}

export interface CockpitDrills {
  readonly onDayBoard: () => void;
  readonly onCommandCenter: () => void;
  readonly onSlaSection: () => void;
}

function CancelReasonList({
  reasons,
}: {
  reasons: readonly CancelReasonBreakdownRow[];
}): JSX.Element {
  return (
    <ul className="cockpit-reasons" data-testid="cockpit-cancel-reasons">
      {reasons.map((r) => (
        <li key={r.code ?? 'no_reason'} className="cockpit-reasons__row">
          <span>{r.label}</span>
          <strong>{r.count}</strong>
        </li>
      ))}
    </ul>
  );
}

/** The presentational cockpit (pure render of a CockpitState). */
export function ManagerOpsCockpitView({
  state,
  drills,
}: {
  state: CockpitState;
  drills: CockpitDrills;
}): JSX.Element {
  const { tiles } = state;
  return (
    <SectionShell
      eyebrow="Manager ops cockpit"
      title="Today command view"
      sub={
        <span data-testid="cockpit-scope">
          {state.scopeLabel} - live counts from today&apos;s clinic day board
        </span>
      }
      tint="teal"
      testId="manager-ops-cockpit"
    >
      {/* ---- MONEY FIRST (design law: money-first visual hierarchy) ---- */}
      <div className="cockpit-tile-row" data-testid="cockpit-money-row">
        {tiles !== null ? (
          <>
            <KpiTile
              label="To collect today"
              value={
                tiles.collect.totalAmount !== null
                  ? inrLabel(tiles.collect.totalAmount)
                  : `${tiles.collect.count} visit${tiles.collect.count === 1 ? '' : 's'}`
              }
              sub={
                tiles.collect.count > 0
                  ? `${tiles.collect.count} visit${tiles.collect.count === 1 ? '' : 's'} awaiting collection - open the day board`
                  : 'Nothing awaiting collection'
              }
              tone={tiles.collect.count > 0 ? 'revenue' : 'ok'}
              onClick={drills.onDayBoard}
              hint="Doctor-accepted plans awaiting collection"
              testId="cockpit-tile-collect"
            />
            <KpiTile
              label="Payments recorded today"
              value={tiles.paidTodayCount}
              sub="Visits with recorded payment evidence"
              tone="ok"
              onClick={drills.onDayBoard}
              testId="cockpit-tile-paid"
            />
          </>
        ) : (
          <p className="cockpit-unavailable" data-testid="cockpit-money-unavailable">
            {state.noScope ? NO_CLINIC_SCOPE_COPY : DAY_TILES_UNAVAILABLE_COPY}
          </p>
        )}
      </div>

      {/* ---- THE DAY FLOW (booked -> in consult -> fulfilled) ---- */}
      {tiles !== null ? (
        <div className="cockpit-tile-row" data-testid="cockpit-flow-row">
          <KpiTile
            label="Booked today"
            value={tiles.bookedToday}
            sub="Today's full slate"
            onClick={drills.onDayBoard}
            testId="cockpit-tile-booked"
          />
          <KpiTile
            label="Arrived"
            value={tiles.arrived}
            onClick={drills.onDayBoard}
            testId="cockpit-tile-arrived"
          />
          <KpiTile
            label="Checked in"
            value={tiles.checkedIn}
            onClick={drills.onDayBoard}
            testId="cockpit-tile-checkedin"
          />
          <KpiTile
            label="In consult"
            value={tiles.inConsult}
            sub="With the doctor now"
            tone={tiles.inConsult > 0 ? 'warm' : 'ok'}
            onClick={drills.onDayBoard}
            hint="Event-derived from the doctor's Start-consult click (draft sheet as fallback)"
            testId="cockpit-tile-inconsult"
          />
          <KpiTile
            label={statusChipLabel('appointment', 'fulfilled')}
            value={tiles.fulfilled}
            onClick={drills.onDayBoard}
            testId="cockpit-tile-fulfilled"
          />
          <KpiTile
            label="Cancelled today"
            value={tiles.cancelled}
            sub={tiles.cancelled > 0 ? 'Reasons below' : undefined}
            tone={tiles.cancelled > 0 ? 'breach' : 'ok'}
            onClick={drills.onDayBoard}
            testId="cockpit-tile-cancelled"
          />
        </div>
      ) : null}

      {/* ---- Cancel-reason breakdown (first consumer of the stored
           cancel_reason_code; a reason-less cancel renders honestly) ---- */}
      {tiles !== null && tiles.cancelReasons.length > 0 ? (
        <CancelReasonList reasons={tiles.cancelReasons} />
      ) : null}

      {/* ---- WHERE WORK LEAKS (escalation + SLA classes) ---- */}
      <div className="cockpit-tile-row" data-testid="cockpit-leak-row">
        {state.escalation !== null ? (
          <KpiTile
            label="Unassigned past escalation"
            value={state.escalation}
            sub="Pool leads over the escalation threshold - open the command center"
            tone={state.escalation > 0 ? 'breach' : 'ok'}
            onClick={drills.onCommandCenter}
            testId="cockpit-tile-escalation"
          />
        ) : (
          <p
            className="cockpit-unavailable"
            data-testid="cockpit-escalation-unavailable"
          >
            {ESCALATION_UNAVAILABLE_COPY}
          </p>
        )}
        {state.slaCounts !== null ? (
          state.slaCounts.map((c) => (
            <KpiTile
              key={c.breachClass}
              label={classMeta(c.breachClass).title}
              value={c.count}
              tone={
                c.severity === 'red'
                  ? 'breach'
                  : c.severity === 'amber'
                    ? 'warm'
                    : 'ok'
              }
              onClick={drills.onSlaSection}
              hint={classMeta(c.breachClass).hint}
              testId={`cockpit-tile-sla-${c.breachClass}`}
            />
          ))
        ) : (
          <p className="cockpit-unavailable" data-testid="cockpit-sla-unavailable">
            {SLA_ROW_UNAVAILABLE_COPY}
          </p>
        )}
      </div>
      {state.slaCapped ? (
        <p className="cockpit-unavailable" data-testid="cockpit-sla-capped">
          {SLA_CAPPED_NOTE_COPY}
        </p>
      ) : null}

      {/* ---- Honest partial-sum note: failed clinics are NAMED ---- */}
      {state.failedClinics.length > 0 ? (
        <p className="cockpit-unavailable" data-testid="cockpit-failed-clinics">
          Not included (day board unavailable): {state.failedClinics.join(', ')}
        </p>
      ) : null}

      {/* ---- All-clinics per-clinic breakdown (the org view pattern:
           each row from ITS own board read; the totals above sum these) ---- */}
      {state.allClinics && state.perClinic.length > 1 ? (
        <table className="cockpit-clinic-table" data-testid="cockpit-clinic-table">
          <thead>
            <tr>
              <th>Clinic</th>
              <th>Booked</th>
              <th>In consult</th>
              <th>{statusChipLabel('appointment', 'fulfilled')}</th>
              <th>Cancelled</th>
              <th>To collect</th>
              <th>Paid</th>
            </tr>
          </thead>
          <tbody>
            {state.perClinic.map((c) => (
              <tr key={c.clinicId}>
                <td>{c.clinicLabel}</td>
                <td>{c.tiles.bookedToday}</td>
                <td>{c.tiles.inConsult}</td>
                <td>{c.tiles.fulfilled}</td>
                <td>{c.tiles.cancelled}</td>
                <td>
                  {c.tiles.collect.totalAmount !== null
                    ? inrLabel(c.tiles.collect.totalAmount)
                    : c.tiles.collect.count}
                </td>
                <td>{c.tiles.paidTodayCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </SectionShell>
  );
}

// ---------------------------------------------------------------------------
// The self-fetching wrapper (fault-isolated; the AGM page mounts this).
// ---------------------------------------------------------------------------

export interface CockpitBoards {
  readonly perClinic: readonly CockpitClinicRow[];
  readonly failed: readonly string[];
}

/** The two board reads the cockpit can make, injected so the REQUEST COUNT
 *  is directly testable (this test plane renders static markup, so an effect
 *  never fires and a counted mount is not available). Production passes the
 *  real api.ts functions and nothing else. */
export interface CockpitBoardFetchers {
  readonly fetchOne: (clinicId: number) => Promise<{
    readonly appointments: readonly TodayAppointment[];
  }>;
  readonly fetchMany: (clinicIds: readonly number[]) => Promise<{
    readonly appointments: readonly TodayAppointment[];
  }>;
}

/**
 * DASHBOARD-FANOUT-AT-119-V1 -- the cockpit's board load, ONE request wide.
 *
 * All-clinics mode used to issue one day-board request PER CLINIC, so a
 * 119-clinic tenant fired 119 parallel requests on every render of this
 * surface. It now issues exactly ONE request whatever the clinic count is,
 * and groups the returned rows by each row's own clinic.
 *
 * SAME NUMBERS: every clinic in scope still gets its own CockpitClinicRow
 * built by the SAME pure deriveDayCommandTiles over the SAME board rows --
 * including a clinic with nothing booked, whose zeroed row the per-clinic
 * path also produced. Only the number of round trips changes.
 *
 * HONEST STATES: a failed read counts no clinic and NAMES every clinic it
 * could not include, so the tiles render their unavailable copy rather than
 * a fake zero (the surface's standing law).
 */
export async function loadCockpitBoards(
  scopeIds: readonly number[],
  labelFor: (clinicId: number) => string,
  fetchers: CockpitBoardFetchers,
): Promise<CockpitBoards> {
  if (scopeIds.length === 0) return { perClinic: [], failed: [] };
  const rowsToPerClinic = (
    byClinic: ReadonlyMap<number, readonly TodayAppointment[]>,
  ): CockpitClinicRow[] =>
    scopeIds.map((clinicId) => ({
      clinicId,
      clinicLabel: labelFor(clinicId),
      tiles: deriveDayCommandTiles(byClinic.get(clinicId) ?? []),
    }));
  try {
    // One clinic in scope keeps the byte-identical single-clinic read.
    if (scopeIds.length === 1) {
      const clinicId = scopeIds[0]!;
      const data = await fetchers.fetchOne(clinicId);
      return {
        perClinic: rowsToPerClinic(new Map([[clinicId, data.appointments]])),
        failed: [],
      };
    }
    const data = await fetchers.fetchMany(scopeIds);
    const byClinic = new Map<number, TodayAppointment[]>();
    for (const clinicId of scopeIds) byClinic.set(clinicId, []);
    for (const row of data.appointments) {
      const bucket =
        typeof row.clinicId === 'number' ? byClinic.get(row.clinicId) : undefined;
      if (bucket !== undefined) bucket.push(row);
    }
    return { perClinic: rowsToPerClinic(byClinic), failed: [] };
  } catch {
    return { perClinic: [], failed: scopeIds.map(labelFor) };
  }
}

/** The production fetchers (the ONE place the real endpoints are named). */
export const COCKPIT_BOARD_FETCHERS: CockpitBoardFetchers = {
  fetchOne: (clinicId) => fetchTodayAppointments(clinicId, 'board'),
  fetchMany: (clinicIds) => fetchTodayAppointmentsForClinics(clinicIds),
};

export default function ManagerOpsCockpit(): JSX.Element {
  const navigate = useNavigate();
  const clinicCtx = useClinicLens();
  const selectedClinicId = clinicCtx?.selectedClinicId ?? null;
  const options: readonly ClinicOption[] = clinicCtx?.options ?? [];
  const optionsKey = options.map((o) => o.clinicId).join(',');

  const [boards, setBoards] = useState<CockpitBoards | null>(null);
  const [tracking, setTracking] = useState<ManagerTrackingView | null>(null);
  const [sla, setSla] = useState<SlaLeakageResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const scopeIds: readonly number[] =
      selectedClinicId !== null
        ? [selectedClinicId]
        : options.map((o) => o.clinicId);
    const labelFor = (clinicId: number): string => {
      const opt = options.find((o) => o.clinicId === clinicId);
      return opt !== undefined ? clinicOptionLabel(opt) : `Clinic ${clinicId}`;
    };

    void Promise.allSettled([
      loadCockpitBoards(scopeIds, labelFor, COCKPIT_BOARD_FETCHERS).then((b) => {
        if (!cancelled) setBoards(b);
      }),
      fetchManagerTracking().then(
        (t) => {
          if (!cancelled) setTracking(t);
        },
        () => {
          if (!cancelled) setTracking(null);
        },
      ),
      fetchSlaLeakage().then(
        (s) => {
          if (!cancelled) setSla(s);
        },
        () => {
          if (!cancelled) setSla(null);
        },
      ),
    ]).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // optionsKey is the stable identity of `options` (derived state).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClinicId, optionsKey]);

  const state: CockpitState = useMemo(() => {
    const perClinic = boards?.perClinic ?? [];
    const tiles =
      perClinic.length === 0
        ? null
        : perClinic.length === 1
          ? perClinic[0]!.tiles
          : sumDayCommandTiles(perClinic.map((c) => c.tiles));
    return {
      scopeLabel:
        selectedClinicId !== null
          ? (clinicCtx?.selectedLabel ?? `Clinic ${selectedClinicId}`)
          : `All clinics (${options.length})`,
      noScope: selectedClinicId === null && options.length === 0,
      tiles,
      perClinic,
      failedClinics: boards?.failed ?? [],
      // Honesty-gated (review finding 2): overlay-off or tracking-fault ->
      // null -> the explicit unavailable copy, never a fake healthy zero.
      escalation: escalationForScope(tracking, selectedClinicId),
      slaCounts:
        sla !== null ? slaClassCountsForScope(sla, selectedClinicId) : null,
      slaCapped: sla !== null && sla.capped === true,
      allClinics: selectedClinicId === null,
    };
  }, [boards, tracking, sla, selectedClinicId, options.length, clinicCtx?.selectedLabel]);

  if (loading) {
    return (
      <SectionShell
        eyebrow="Manager ops cockpit"
        title="Today command view"
        tint="teal"
        testId="manager-ops-cockpit"
      >
        <p className="text-sm text-muted-foreground" data-testid="cockpit-loading">
          Loading today&apos;s command view...
        </p>
      </SectionShell>
    );
  }

  return (
    <ManagerOpsCockpitView
      state={state}
      drills={{
        onDayBoard: () => navigate('/walkin'),
        onCommandCenter: () => navigate('/dashboard/lead-cockpit'),
        onSlaSection: () => {
          document
            .getElementById(SLA_SECTION_ANCHOR_ID)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },
      }}
    />
  );
}
